import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { MdAttachMoney } from "react-icons/md";

const MonthlyCostChart = ({ reportData, loading }) => {
  const parseFinancialData = (financialDataStr) => {
    if (!financialDataStr) return [];
    try {
      const parsed = JSON.parse(financialDataStr);
      return parsed;
    } catch {
      return [];
    }
  };

  const buildStackedCostData = (financialData) => {
    return financialData.map(vm => ({
      name: vm.vmName,
      DirectCost: parseFloat(vm.directCost) || 0,
      ReservationCost: parseFloat(vm.reservationCost) || 0,
      SharedCost: parseFloat(vm.sharedCost) || 0,
      totalCost: parseFloat(vm.totalCost) || 0
    }));
  };

  const financialData = parseFinancialData(reportData?.financialData);
  const chartData = buildStackedCostData(financialData);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + entry.value, 0);
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
          <p className="text-white text-xs sm:text-sm font-bold mb-2 break-words">{label}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold mb-2">Total: {formatCurrency(total)}</p>
          <div className="space-y-1">
            {payload.map((entry, idx) => (
              entry.value > 0 && (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-gray-300 text-[10px] sm:text-xs break-words">{entry.name}:</span>
                  <span className="text-white text-[10px] sm:text-xs font-semibold">{formatCurrency(entry.value)}</span>
                </div>
              )
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="mt-4 sm:mt-6 bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
        <div className="h-80 sm:h-96"></div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="mt-4 sm:mt-6 bg-primary-dark rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <MdAttachMoney className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Monthly Cost Breakdown by VM</h3>
        </div>
        <div className="text-center py-8 sm:py-12">
          <p className="text-gray-400 text-sm sm:text-base">No financial data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 sm:mt-6 bg-primary-dark rounded-xl p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
        <MdAttachMoney className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Monthly Cost Breakdown by VM</h3>
      </div>
      <div className="w-full overflow-x-auto">
        <div className="min-w-[500px]" style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                angle={-25}
                textAnchor="end"
                height={70}
                interval={0}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                tickFormatter={(value) => `$${value}`}
                label={{ value: "Cost (USD)", angle: -90, position: "insideLeft", fill: "#9CA3AF", fontSize: 10 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend
                wrapperStyle={{ fontSize: "10px", paddingTop: "15px" }}
                iconType="circle"
                formatter={(value) => <span className="text-gray-300 text-[10px] sm:text-xs">{value}</span>}
              />
              <Bar
                dataKey="DirectCost"
                stackId="cost"
                fill="#dc2626"
                animationDuration={1000}
                cursor={false}
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="ReservationCost"
                stackId="cost"
                fill="#3b82f6"
                animationDuration={1000}
                cursor={false}
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="SharedCost"
                stackId="cost"
                fill="#10b981"
                animationDuration={1000}
                cursor={false}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MonthlyCostChart;